import { type Address, type Hex } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import {
  createRhinestoneAccount,
  type Session,
  type SignData,
  type SignerSet,
} from "@rhinestone/sdk";
import { toSession } from "@rhinestone/sdk/smart-sessions";
import { getChainById } from "./registry";
import type { HeadlessPrepareResult, SessionKeyHandle } from "./types";

/** Restores bigint sentinel strings emitted by 1auth's JSON transport. */
function reviveBigIntStrings<T>(value: T): T {
  if (Array.isArray(value)) return value.map((entry) => reviveBigIntStrings(entry)) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, reviveBigIntStrings(entry)]),
    ) as T;
  }
  if (typeof value === "string" && value.startsWith("__bigint:")) {
    return BigInt(value.slice("__bigint:".length)) as T;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) return BigInt(value) as T;
  return value;
}

/** Parses the prepared transaction enough to recover the SDK-native SignData. */
function parsePreparedSignData(value: string): SignData {
  const prepared = JSON.parse(value, (_key, nested) => reviveBigIntStrings(nested)) as {
    quotes?: { best?: { signData?: SignData } };
  };
  const signData = prepared.quotes?.best?.signData;
  if (!signData) {
    throw new Error("Prepared headless intent is missing quotes.best.signData");
  }
  return signData;
}

/** Reads the numeric EIP-712 chain id from a typed-data domain. */
function chainIdFromSignDataDomain(domainChainId: unknown): number | undefined {
  if (typeof domainChainId === "number" && Number.isSafeInteger(domainChainId)) {
    return domainChainId;
  }
  if (typeof domainChainId === "bigint") return Number(domainChainId);
  if (typeof domainChainId === "string" && /^\d+$/.test(domainChainId)) {
    return Number(domainChainId);
  }
  return undefined;
}

/** Rebuilds the SDK SmartSession object from the persisted grant handle. */
function buildSessionForChain(args: {
  privateKey: Hex;
  handle: SessionKeyHandle;
  chainId: number;
}): Session {
  return toSession(
    {
      chain: getChainById(args.chainId),
      owners: { type: "ecdsa", accounts: [privateKeyToAccount(args.privateKey)] },
      permissions: reviveBigIntStrings(args.handle.permissions ?? []),
      crossChainPermits: reviveBigIntStrings(args.handle.crossChainPermits ?? []),
    },
    // The production SmartSession module is deployed on testnets too. Never opt
    // into SDK dev contracts for 1auth headless permissions.
    { useDevContracts: false },
  );
}

/** Builds a per-chain SmartSession signer set for every chain in SignData. */
function buildSessionSigners(args: {
  privateKey: Hex;
  handle: SessionKeyHandle;
  prepared: HeadlessPrepareResult;
  signData: SignData;
}): SignerSet {
  const chainIds = new Set<number>([args.prepared.targetChain]);
  for (const typedData of args.signData.origin) {
    const chainId = chainIdFromSignDataDomain(typedData.domain?.chainId);
    if (chainId) chainIds.add(chainId);
  }
  const targetExecutionChainId = chainIdFromSignDataDomain(
    args.signData.targetExecution?.domain?.chainId,
  );
  if (targetExecutionChainId) chainIds.add(targetExecutionChainId);

  return {
    type: "experimental_session",
    sessions: Object.fromEntries(
      [...chainIds].map((chainId) => [
        chainId,
        {
          session: buildSessionForChain({
            privateKey: args.privateKey,
            handle: args.handle,
            chainId,
          }),
        },
      ]),
    ),
  };
}

export interface BuildSmartSessionHeadlessSignaturesOptions {
  /** ECDSA private key for the locally-held SmartSession signer. */
  privateKey: Hex;
  /** Smart account address the SmartSession is scoped to. */
  accountAddress: Address;
  /** Persisted handle returned by `grantPermissions` / SmartSession install. */
  sessionKeyHandle: SessionKeyHandle;
  /** Prepared headless intent returned by `OneAuthHeadlessClient.prepareIntent`. */
  prepared: HeadlessPrepareResult;
}

/**
 * Builds SmartSession signatures for `OneAuthHeadlessClient.submitIntent`.
 *
 * This intentionally delegates signing and SmartSession signature packing to
 * `@rhinestone/sdk`. 1auth only rebuilds the persisted session descriptor and
 * supplies the SDK-native `SignData` returned by the orchestrator quote.
 */
export async function buildSmartSessionHeadlessSignatures(
  options: BuildSmartSessionHeadlessSignaturesOptions,
) {
  const signData = parsePreparedSignData(options.prepared.intentOp);
  const account = await createRhinestoneAccount({
    initData: { address: options.accountAddress },
    owners: {
      type: "ecdsa",
      accounts: [privateKeyToAccount(options.privateKey)],
    },
    experimental_sessions: { enabled: true },
  });

  return account.signIntent(
    signData,
    getChainById(options.prepared.targetChain),
    buildSessionSigners({
      privateKey: options.privateKey,
      handle: options.sessionKeyHandle,
      prepared: options.prepared,
      signData,
    }),
  );
}
